import { addDays, parse, format, isValid, isWithinInterval, startOfDay } from 'date-fns';
import type { ChangeEvent } from 'react';
import type { DateFormat } from '../types/dateFormat';

function isValidJsDate(date: unknown) {
  const isDate = date instanceof Date;
  const isCorrectlyConstructedDate = isDate && !Number.isNaN(date.getTime());

  return isCorrectlyConstructedDate;
}

const dateFormatConfig = {
  'MM/dd/yyyy': {
    lengths: { first: 2, second: 2, third: 4 },
    separators: { first: '/', second: '/', third: '' },
  },
  'dd/MM/yyyy': {
    lengths: { first: 2, second: 2, third: 4 },
    separators: { first: '/', second: '/', third: '' },
  },
  'dd-MM-yyyy': {
    lengths: { first: 2, second: 2, third: 4 },
    separators: { first: '-', second: '-', third: '' },
  },
  'yyyy-MM-dd': {
    lengths: { first: 4, second: 2, third: 2 },
    separators: { first: '-', second: '-', third: '' },
  },
  yyyy年MM月dd日: {
    lengths: { first: 4, second: 2, third: 2 },
    separators: { first: '年', second: '月', third: '日' },
  },
  yyyy년MM월dd일: {
    lengths: { first: 4, second: 2, third: 2 },
    separators: { first: '년', second: '월', third: '일' },
  },
};

const convertToTwoDigits = (segment: string, length: number) => {
  if (length > 2) {
    return segment;
  }

  if (segment?.length + 1 === length) {
    return `0${segment}`;
  }

  return segment;
};

export const getParsedDate = (date: string, dateFormat: string) => {
  return parse(date, dateFormat, new Date());
};

export const convertToTwoDigitFormat = (
  event: ChangeEvent<HTMLInputElement>,
  dateFormat: DateFormat,
) => {
  const { separators, lengths } = dateFormatConfig[dateFormat];
  const { value } = event.target;

  let firstSegment = '';
  let secondSegment = '';
  let thirdSegment = '';

  if (separators.third === '') {
    [firstSegment, secondSegment, thirdSegment] = value.split(separators.first);
  } else {
    [firstSegment, secondSegment, thirdSegment] = value
      .split(separators.first)
      .join(separators.second)
      .split(separators.second)
      .join(separators.third)
      .split(separators.third);
  }

  firstSegment = convertToTwoDigits(firstSegment, lengths.first);
  secondSegment = convertToTwoDigits(secondSegment, lengths.second);
  thirdSegment = convertToTwoDigits(thirdSegment, lengths.third);

  let formattedValue = firstSegment;

  if (secondSegment) {
    formattedValue += `${separators.first}${secondSegment}`;
  }

  if (thirdSegment) {
    formattedValue += `${separators.second}${thirdSegment}${separators.third}`;
  }
  return formattedValue;
};

export const getFormattedValueFromString = (value: string, dateFormat: DateFormat) => {
  const cleanValue = value.replace(/\D/g, '');
  const config = dateFormatConfig[dateFormat];
  const firstSeparator = config.separators.first;
  const secondSeparator = config.separators.second;

  const firstLength = config.lengths.first;
  const secondLength = config.lengths.second;
  const thirdLength = config.lengths.third;

  const firstSegment = cleanValue.slice(0, firstLength);
  const secondSegment = cleanValue.slice(firstLength, firstLength + secondLength);
  const thirdSegment = cleanValue.slice(
    firstLength + secondLength,
    firstLength + secondLength + thirdLength,
  );

  let formattedValue = firstSegment;

  if (secondSegment) {
    formattedValue += `${firstSeparator}${secondSegment}`;
  }

  const thirdSeparator = thirdLength === thirdSegment.length ? config.separators.third : '';

  if (thirdSegment) {
    formattedValue += `${secondSeparator}${thirdSegment}${thirdSeparator}`;
  }

  return formattedValue;
};

export const getFormattedValue = (value: Date | string, dateFormat: DateFormat) => {
  const isUserInputChange = typeof value === 'string';

  if (isUserInputChange) {
    return getFormattedValueFromString(String(value), dateFormat);
  }

  if (isValidJsDate(value)) {
    return format(value, dateFormat);
  }

  return '';
};

export const isDateInputAllowed = (date: string, dateFormat: string) => {
  const separators = [...new Set(dateFormat.replace(/y+|M+|d+/g, ''))].join('');

  if (date.length === 0) {
    return true;
  }

  if (date.replace(/[^0-9]/g, '').length >= 9) {
    return false;
  }

  return new RegExp(`^[0-9${separators}]+$`).test(date);
};

export const isDateWithinInterval = (date: Date) => {
  const minDate = new Date();
  const maxDate = addDays(minDate, 365);
  return isWithinInterval(startOfDay(date), {
    end: startOfDay(maxDate),
    start: startOfDay(minDate),
  });
};

export const isDateFormatValid = (date: string, dateFormat: string) => {
  const parsedDate = getParsedDate(date, dateFormat);

  return isValid(parsedDate) && date === format(parsedDate, dateFormat);
};
